import React, { useState, useEffect, useMemo } from 'react';
import { apiUrls, authFetch } from '../config/api';

interface Sentence {
  Text: string;
  audiourl: string;
  translations?: string[];
  aiexplain?: string;
  soundmark?: string;
}

interface SubCategoryGridProps {
  category: string;
  onSubCategorySelect: (subCategory: string, audioData: Sentence[], subIndex: number, totalSubs: number) => void;
  onBack: () => void;
}

const SubCategoryGrid: React.FC<SubCategoryGridProps> = ({ category, onSubCategorySelect, onBack }) => {
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'grid' | 'range'>('grid');
  const [selectedRange, setSelectedRange] = useState({ start: 1, end: 100 });
  
  const ITEMS_PER_SUBCATEGORY = 20;
  const totalSubCategories = useMemo(() => Math.ceil(totalCount / ITEMS_PER_SUBCATEGORY), [totalCount]);

  // 预定义范围选项
  const rangeOptions = useMemo(() => {
    const ranges = [];
    const step = 100;
    for (let i = 0; i < totalSubCategories; i += step) {
      const start = i + 1;
      const end = Math.min(i + step, totalSubCategories);
      ranges.push({ start, end, label: `${start}-${end}` });
    }
    return ranges;
  }, [totalSubCategories]);

  useEffect(() => {
    const abortController = new AbortController();
    
    const fetchCategoryData = async () => {
      try {
        setLoading(true);
        const response = await authFetch(apiUrls.sentences({ category, limit: 1 }), {
          signal: abortController.signal
        });
        const result = await response.json();
        
        if (result.success) {
          setTotalCount(result.data.total_count);
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Failed to fetch category data:', error);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchCategoryData();
    
    return () => {
      abortController.abort();
    };
  }, [category]);

  const handleSubCategoryClick = async (index: number) => {
    try {
      const subCategory = `${category} - ${index + 1}`;
      const response = await authFetch(apiUrls.sentences({ category, page: index, limit: ITEMS_PER_SUBCATEGORY }));
      const result = await response.json();
      
      if (result.success) {
        onSubCategorySelect(subCategory, result.data.sentences, index, totalSubCategories);
      }
    } catch (error) {
      console.error('Failed to fetch subcategory data:', error);
    }
  };

  const renderGridView = () => {
    const { start, end } = selectedRange;
    const items = [];
    
    for (let i = start - 1; i < Math.min(end, totalSubCategories); i++) {
      items.push(
        <button
          key={i}
          onClick={() => handleSubCategoryClick(i)}
          className="p-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm"
        >
          <div className="font-medium">{category} - {i + 1}</div>
          <div className="text-xs opacity-75">
            {i * ITEMS_PER_SUBCATEGORY + 1}-{Math.min((i + 1) * ITEMS_PER_SUBCATEGORY, totalCount)}
          </div>
        </button>
      );
    }
    
    return (
      <div className="grid grid-cols-2 gap-2 max-h-96 overflow-y-auto">
        {items}
      </div>
    );
  };

  const renderRangeView = () => (
    <div className="space-y-2 max-h-96 overflow-y-auto">
      {rangeOptions.map((range) => (
        <button
          key={range.label}
          onClick={() => {
            setSelectedRange(range);
            setViewMode('grid');
          }}
          className="w-full p-4 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          <div className="flex justify-between items-center">
            <span className="font-medium">Groups {range.label}</span>
            <span className="text-sm opacity-75">
              {(range.start - 1) * ITEMS_PER_SUBCATEGORY + 1} - {Math.min(range.end * ITEMS_PER_SUBCATEGORY, totalCount)} sentences
            </span>
          </div>
        </button>
      ))}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen p-4">
      <div className="max-w-lg w-full">
        <div className="bg-white rounded-lg p-6 shadow-lg">
          <div className="flex items-center mb-4">
            <button
              onClick={onBack}
              className="mr-4 p-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
            >
              ← Back
            </button>
            <h1 className="text-lg font-bold">{category}</h1>
          </div>
          
          <div className="text-center mb-4 text-sm text-gray-600">
            Total: {totalCount.toLocaleString()} sentences ({totalSubCategories.toLocaleString()} groups)
          </div>

          {/* View Mode Toggle */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setViewMode('range')}
              className={`px-4 py-2 rounded text-sm ${viewMode === 'range' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
            >
              📋 Browse Ranges
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`px-4 py-2 rounded text-sm ${viewMode === 'grid' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
            >
              🔢 Groups {selectedRange.start}-{selectedRange.end}
            </button>
          </div>

          {/* Quick Jump (only in grid mode) */}
          {viewMode === 'grid' && (
            <div className="mb-4">
              <div className="flex gap-1 text-xs mb-2">
                <button onClick={() => setSelectedRange({ start: 1, end: 100 })} className="px-2 py-1 bg-green-100 text-green-600 rounded">1-100</button>
                <button onClick={() => setSelectedRange({ start: 101, end: 200 })} className="px-2 py-1 bg-green-100 text-green-600 rounded">101-200</button>
                <button onClick={() => setSelectedRange({ start: Math.max(1, totalSubCategories - 99), end: totalSubCategories })} className="px-2 py-1 bg-green-100 text-green-600 rounded">Last 100</button>
              </div>
              <input
                type="number"
                placeholder="Jump to group number..."
                min="1"
                max={totalSubCategories}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    const num = parseInt((e.target as HTMLInputElement).value);
                    if (num >= 1 && num <= totalSubCategories) {
                      const start = Math.max(1, num - 50);
                      const end = Math.min(totalSubCategories, num + 49);
                      setSelectedRange({ start, end });
                    }
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              />
            </div>
          )}

          {/* Content */}
          {viewMode === 'range' ? renderRangeView() : renderGridView()}
        </div>
      </div>
    </div>
  );
};

export default SubCategoryGrid;
